import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { BasicInformationBaseComponent } from 'src/app/base-components/basic-information-base.component';
import { EmploymentAgreementDto } from 'src/app/models/dtos/employment-agreement-dto';
import { EmploymentConditionsConfirmationService } from 'src/app/data/employment-conditions-confirmation.service';
import { combineLatest, distinctUntilChanged, EMPTY, first, firstValueFrom, startWith, switchMap, takeUntil } from 'rxjs';
import { LegalizationDataForWorkerAgreementDto } from 'src/app/models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { MultipleWorkerAgreementsActionSelectedWorkersDto } from 'src/app/models/dtos/multiple-worker-agreements-action-selected-workers-dto';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { Messages } from '../../../common/enums';
import { NewContractDurationUnit } from '../../../models/enums/new-contract-duration-unit.enum';

@Component({
  selector: 'app-multiple-basic-information-step',
  templateUrl: './multiple-basic-information-step.component.html',
  styleUrls: ['./multiple-basic-information-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MultipleBasicInformationStepComponent extends BasicInformationBaseComponent implements OnInit, OnChanges, OnDestroy {
  private employmentConditionsConfirmationService = inject(EmploymentConditionsConfirmationService);
  readonly contractDurationUnitEnum = NewContractDurationUnit;

  @Input() isExtension: boolean;
  @Input() selectedWorkers: MultipleWorkerAgreementsActionSelectedWorkersDto[];
  @Output() formGroupChange: EventEmitter<EmploymentAgreementDto> = new EventEmitter();
  @Output() selectedWorkersChange = new EventEmitter<MultipleWorkerAgreementsActionSelectedWorkersDto[]>();

  foreignWorkers = new Set<number>();
  workerAgreementLegalizationData: LegalizationDataForWorkerAgreementDto[] | null = null;
  foreignWorkersWithData: Array<{ id: number; name: string; legalizationData: LegalizationDataForWorkerAgreementDto }> = [];
  isLegalizationVisible = false;
  isSyntheticSelectedCompany = false;

  constructor() {
    super();

    this.employmentTypeId = this.route.snapshot.params.employmentTypeId;
    this.listOfAgreementTypes$ = this.dictionaryService.getAgreementTypes([this.employmentTypeId]);
  }

  get isSubmitForLegalizationEnabled(): boolean {
    const formData = this.basicInformationStepFormGroup.getRawValue();
    return !!(formData.company && formData.employer && formData.employerObject);
  }

  get isPlannedPeriodSectionVisible(): boolean {
    const employmentDateFrom = this.basicInformationStepFormGroup.get('employmentDateFrom')?.value;
    const employmentDateTo = this.basicInformationStepFormGroup.get('employmentDateTo')?.value;
    const maxDurationMonths = this.maxDurationMonths ?? null;

    return this.isPlannedPeriodSectionVisibleForDates(employmentDateFrom, employmentDateTo, maxDurationMonths);
  }

  async ngOnInit(): Promise<void> {
    await this.handleSubscriptionModules();
    this.onAgreementTypeChange();
    this.onCompanyChange();
    this.observeSelectedCompanyChanges();
    this.onLocationChange();
    this.onSupervisorChange();
    this.onTemporaryWorkplaceChange();
    this.onJobTitleChange();
    this.initEmployerAutoCompletes();
    this.onBasicInformationStepFormGroupChange();
    this.onEmployerChange();
    this.onEmployerOrCompanyChange();
    this.setLegalizationInformation();
    this.onSupervisorJobTitleChange();
    this.initializePlannedPeriodControls();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onPrefetchedAgreementChanges(changes);
    this.recalculatePlannedPeriodState();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  setLegalizationInformation(): void {
    if (this.isPermittedToLegalization()) {
      this.workerService.getWorkerDataForAgreementForm(this.selectedWorkers.map((w) => w.Id))
        .pipe(first())
        .subscribe(async (res) => {
          this.foreignWorkers = new Set(res.filter((worker) => worker.IsForeigner).map((worker) => worker.WorkerId));
          this.workerAgreementLegalizationData = res.map((dto) => ({
            WorkerId: dto.WorkerId,
            LegalizationStatusName: null,
            LegalizationStatusId: null,
          }));

          const companies = await firstValueFrom(this.listOfCompanies$);
          this.selectedCompany = companies.find((c) => c.Id === this.company.value) || null;
        });
    }
  }

  get selectedCompanyName(): string {
    return this.selectedCompany?.Name ?? '';
  }

  submit = () => {
    this.normalizePlannedPeriodFieldsForSubmit();
    this.basicInformationStepFormGroup.updateValueAndValidity({ emitEvent: false });
    this.basicInformationStepFormGroup.markAllAsTouched();
    if (this.basicInformationStepFormGroup.invalid) {
      return;
    }

    this.formGroupChange.emit(this.updateBasicInformationStep(this.basicInformationStepFormGroup.getRawValue()));
  };

  private normalizePlannedPeriodFieldsForSubmit(): void {
    if (this.isPlannedPeriodSectionVisible) {
      this.contractDurationValue.enable({ emitEvent: false });
      this.contractDurationUnit.enable({ emitEvent: false });
      return;
    }

    this.contractDurationValue.setValue(null, { emitEvent: false });
    this.contractDurationUnit.setValue(null, { emitEvent: false });
    this.contractDurationValue.setErrors(null);
    this.contractDurationUnit.setErrors(null);
  }

  isLegalizationFieldVisible(): boolean {
    return this.foreignWorkers.size > 0 && this.selectedCompany?.HasLegalization;
  }

  onSubmitForLegalization(): void {
    if (!this.isSubmitForLegalizationEnabled) {
      this.markRequiredLegalizationFieldsAsInvalid();
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSubmitForMultipleLegalization } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? super.submitForMultipleLegalizations(this.selectedWorkers.map((w) => w.Id)) : EMPTY)),
      )
      .subscribe((processedWorkerIds: number[]) => {
        if (processedWorkerIds && processedWorkerIds.length > 0) {
          this.selectedWorkers = this.selectedWorkers.filter((worker) => !processedWorkerIds.includes(worker.Id));

          if (this.selectedWorkers.length > 0) {
            sessionStorage.setItem('selectedWorkers', JSON.stringify(this.selectedWorkers));
            this.selectedWorkersChange.emit(this.selectedWorkers);
            this.foreignWorkers = new Set<number>([...this.foreignWorkers].filter((num) => !processedWorkerIds.includes(num)));
            this.refreshLegalizationView();
          } else {
            sessionStorage.removeItem('selectedWorkers');
            this.router.navigate(['/workerAgreements']);
          }
        }
      });
  }

  private markRequiredLegalizationFieldsAsInvalid(): void {
    const requiredFields = ['company', 'employer', 'employerObject'];

    requiredFields.forEach((fieldName) => {
      const control = this.basicInformationStepFormGroup.get(fieldName);
      if (!control.value) {
        control.setErrors({ required: true });
        control.markAsTouched();
      }
    });
  }

  private onEmployerOrCompanyChange(): void {
    combineLatest([this.employer.valueChanges.pipe(startWith(this.employer.value)), this.company.valueChanges.pipe(startWith(this.company.value))])
      .pipe(distinctUntilChanged())
      .pipe(
        switchMap(([employerId, companyId]) =>
          this.employmentConditionsConfirmationService.checkEmploymentConditionsConfirmations(employerId, companyId),
        ),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((hasEmploymentConditionsConfirmations) => {
        this.hasEmploymentConditionsConfirmations.setValue(hasEmploymentConditionsConfirmations);
      });
  }

  private observeSelectedCompanyChanges(): void {
    combineLatest([this.listOfCompanies$, this.company.valueChanges.pipe(startWith(this.company.value))])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        void this.resolveSelectedCompany();
      });
  }

  private async resolveSelectedCompany(): Promise<void> {
    const companies = await firstValueFrom(this.listOfCompanies$);
    const companyIdFromForm = Number(this.company.value);
    const companyIdFromPrefetch = Number(this.prefetchedAgreement?.CompanyId);
    const companyIdFromSelection = Number(this.selectedWorkers?.[0]?.CompanyId);
    const companyNameFromSelection = this.selectedWorkers?.[0]?.CompanyName;

    const candidateId =
      companyIdFromForm > 0
        ? companyIdFromForm
        : companyIdFromPrefetch > 0
          ? companyIdFromPrefetch
          : companyIdFromSelection > 0
            ? companyIdFromSelection
            : null;

    const selectedById = candidateId ? companies.find((c) => c.Id === candidateId) : null;
    const selectedByName = !selectedById && companyNameFromSelection
      ? companies.find((c) => c.Name === companyNameFromSelection)
      : null;

    const syntheticCompany = candidateId
      ? {
        Id: candidateId,
        Name: companyNameFromSelection || '',
        HasLegalization: false,
        HasMinimalWageValidation: false,
        RequiredPPKSign: false,
      }
      : null;

    this.selectedCompany = selectedById || selectedByName || syntheticCompany;
    this.isSyntheticSelectedCompany = !selectedById && !selectedByName && !!syntheticCompany;

    if (selectedById && this.company.value !== selectedById.Id) {
      this.company.setValue(selectedById.Id, { emitEvent: false });
    }

    this.refreshLegalizationView();
  }

  private refreshLegalizationView(): void {
    this.isLegalizationVisible = this.foreignWorkers.size > 0 && !!this.selectedCompany?.HasLegalization;

    if (!this.workerAgreementLegalizationData || this.foreignWorkers.size === 0) {
      this.foreignWorkersWithData = [];
      return;
    }

    this.foreignWorkersWithData = this.selectedWorkers
      .filter((worker) => this.foreignWorkers.has(worker.Id))
      .map((worker) => ({
        id: worker.Id,
        name: worker.Name,
        legalizationData: this.workerAgreementLegalizationData!.find((data) => data.WorkerId === worker.Id),
      }))
      .filter((item): item is { id: number; name: string; legalizationData: LegalizationDataForWorkerAgreementDto } => !!item.legalizationData);
  }
}
